import { useState } from 'react';
import {
  apiInvestigados,
  TIPOS_DE_DADO,
  type ConsultaExterna,
  type DadoDoInvestigado,
  type GrauDeParentesco,
  type Investigado,
  type QualificacaoInvestigado,
  type TipoDoDado,
} from '../lib/api.ts';
import { avisar, avisarErro } from './Avisos.tsx';
import { Confirmar } from './Confirmar.tsx';
import {
  TITULO_DA_EVOLUCAO,
  apiRelatorios,
  type AnotacaoDaSecao,
  type EvolucaoDaEmpresa,
} from '../lib/api';
import { Icone } from './Icone.tsx';
import { BlocoDeLancar, CampoDoPasso } from './PassoAPasso.tsx';

/*
 * ⭐ **ESTE ARQUIVO DEIXOU DE SER UMA TELA — 19/09/2026.** O componente
 * `PartesInvestigadas` saiu: a Qualificação das partes está no desenho de
 * Imóveis (`QualificacaoDoCaso`, PRD §46.6). **Ficam aqui as peças que ela
 * usa** — a fila da busca, a lista de dados, o acrescentar dado e vínculo,
 * trazer para o caso, os atos societários. O comentário abaixo é a história das
 * regras que elas continuam seguindo.
 */

/**
 * As partes investigadas, dentro da seção **Investigados** do relatório.
 *
 * > *"Incluir subtítulos abaixo de cada investigado: QUALIFICAÇÃO e CONEXÕES,
 * > ambos com a setinha. Dentro da seção deverá ter um botão (busca) que
 * > buscará através da Assertiva API. (…) Gostaria que houvesse um check box
 * > antes dos nomes, onde eu possa selecionar quais dos investigados eu quero
 * > que busque."* — Richard, 20/08/2026
 *
 * ## A divisão, e por que ela é a que é
 *
 * | Subtítulo | O que recebe |
 * |---|---|
 * | **QUALIFICAÇÃO** | o que descreve a PESSOA — nascimento, filiação, telefones, e-mails, endereços, profissões |
 * | **CONEXÕES** | o que a liga a OUTREM — parentes, pessoas de referência, empresas |
 *
 * ⚠️ **A régua é "descreve" contra "liga".** Um telefone descreve o
 * investigado; um sócio o liga a alguém. Sem essa régua, "pessoas de
 * referência" — que são contatos dele — cairiam em qualificação, e ali elas se
 * perderiam entre telefones em vez de aparecerem como o que são: gente ligada a
 * ele, que pode virar investigado.
 *
 * ⚠️ **Só o MASTER busca.** Cada investigado custa cerca de R$ 1,21, mais
 * R$ 1,00 por empresa encontrada — e o colaborador não vê valor. Pedir que ele
 * autorize um gasto cujo tamanho não enxerga é pedir decisão às cegas.
 *
 * ⚠️ **Veículos NÃO entra neste botão** — decisão do Richard: *"posteriormente
 * teremos uma seção VEÍCULOS e aí sim terá esse botão"*. Custa R$ 15,41 por
 * CPF, treze vezes o resto junto.
 */

/*
 * ⚠️ **A ordem é de responsabilidade, não alfabética — e o sócio entra ANTES da
 * empresa em 07/09/2026.** Numa execução, quem responde com o próprio
 * patrimônio se lê primeiro. É a mesma ordem do quadro de ativos por
 * qualificação, em `lib/resumo-de-ativos.ts`: se as duas discordassem, a mesma
 * pessoa apareceria em posições diferentes na tela e no papel.
 */
export const ORDEM: QualificacaoInvestigado[] = [
  'devedor',
  'avalista',
  'socio_nao_avalista',
  'grupo_economico',
  'grupo_familiar',
];

export const O_QUE_ABRIGA: Record<QualificacaoInvestigado, string> = {
  devedor: 'quem responde pela dívida',
  avalista: 'quem garantiu a dívida',
  socio_nao_avalista: 'sócio que não assinou como garantidor',
  grupo_economico: 'empresas ligadas ao devedor',
  grupo_familiar: 'genitores, cônjuges, filhos, irmãos e primos',
};

/**
 * O que a busca custa por investigado, em reais.
 *
 * ⚠️ **Escrito no botão, e não escondido.** Ficha cadastral (R$ 1,00) +
 * vínculos (R$ 0,206) + pessoas de referência (R$ 1,00). O dossiê de cada
 * empresa encontrada custa mais R$ 1,00 e não dá para prever antes — por isso o
 * botão diz "a partir de".
 */
export const CUSTO_POR_INVESTIGADO = 1.206;

/**
 * Os tipos que descrevem a pessoa. O resto da lista vive em CONEXÕES.
 *
 * ⚠️ **A ordem desta lista É a ordem na tela**, e ela tem argumento: primeiro
 * quem a pessoa É — estado civil, regime de bens, óbito, situação cadastral,
 * logo abaixo do nascimento e da filiação —, depois como se fala com ela, e por
 * fim o que ela faz. Alfabética poria o e-mail antes do óbito.
 *
 * ⚠️ **Os quatro do começo entraram em 21/08/2026, a pedido do Richard.** Dois
 * deles a Assertiva já devolvia dentro de `outro`, sem rótulo: situação
 * cadastral e indício de óbito. Estado civil e regime de bens ela não devolve —
 * são de digitar à mão, ou de ler no contrato social.
 */
/**
 * As fontes que ESTA seção aciona — e portanto as únicas cujas falhas ela mostra.
 *
 * ⚠️ **A lista existe porque a ausência dela deixou falha vazar entre seções**
 * em 21/08/2026. Fonte nova que a seção passe a consultar entra aqui; fonte de
 * outra seção **não** entra, por mais que a consulta seja do mesmo investigado.
 */
const FONTES_DESTA_SECAO = new Set(['assertiva', 'receita-cnpj', 'datajud', 'ficha']);

/**
 * Ordena as participações societárias — pedido do Richard, 21/08/2026.
 *
 * > *"Ser por ordem de 0001-, 0002-, 0003-, 0004-, e assim por diante. A
 * > primeira regra de ordem é data de abertura da empresa (mais antigas
 * > primeiro e mais novas pra baixo)."*
 *
 * ⭐ **Os quatro dígitos de ordem do CNPJ JÁ são a data de abertura**, dentro da
 * mesma empresa: a Receita os atribui em sequência conforme cada
 * estabelecimento abre. `0001` é a matriz — a mais antiga por definição —, e
 * cada filial recebe o número seguinte. **Ordenar por eles é ordenar por
 * abertura, sem precisar consultar a data.**
 *
 * ⚠️ **Entre empresas DIFERENTES isso não vale**, e a ordenação cai na razão
 * social. Para pôr a empresa mais antiga acima da mais nova seria preciso a
 * data de abertura de cada uma — que o Hub ainda não guarda. Fica dito em vez
 * de fingido.
 *
 * ⚠️ **Ordena a EXIBIÇÃO, não o banco.** Nada é apagado nem reescrito: a ficha
 * mostra as mesmas linhas, em ordem legível.
 */
export function ordenarParticipacoes(linhas: DadoDoInvestigado[]): DadoDoInvestigado[] {
  const digitos = (d: DadoDoInvestigado) =>
    (d.valor.match(/\d[\d./-]{12,17}\d/)?.[0] ?? '').replace(/\D+/g, '');
  return [...linhas].sort((a, b) => {
    const da = digitos(a);
    const db = digitos(b);
    /* Mesma empresa: manda a ordem do estabelecimento. */
    if (da.length === 14 && db.length === 14 && da.slice(0, 8) === db.slice(0, 8)) {
      return da.slice(8, 12).localeCompare(db.slice(8, 12));
    }
    /* Empresas diferentes: pela razão social, que é o que se tem. */
    return a.valor.localeCompare(b.valor, 'pt-BR');
  });
}

export const DA_QUALIFICACAO: TipoDoDado[] = [
  'estado_civil',
  'regime_de_bens',
  'obito',
  /* ⭐ Os quatro da PESSOA JURÍDICA — 09/09/2026. Na mesma régua dos de pessoa
     física: primeiro quem a pessoa É, depois como se fala com ela. */
  'natureza_juridica',
  'constituicao',
  'situacao_cadastral',
  'atividade_principal',
  'atividade_secundaria',
  'telefone',
  'email',
  'endereco',
  'profissao',
  'historico_profissional',
  'participacao_societaria',
  'ppe',
  'rede_social',
  'probabilidade_negociacao',
  'processo',
  'outro',
];

/**
 * Quais tipos são **dado técnico** e saem em DM Mono. O resto é prosa e sai em
 * DM Sans.
 *
 * ⚠️ **Escrito como lista, e não como condição no meio do JSX**, porque a
 * condição já tinha dois tipos e ia ganhar mais quatro — e a regra do
 * `CLAUDE.md` é clara: *"prosa não usa DM Mono, nem herdada"*. Estado civil,
 * regime de bens e situação cadastral são palavras, não números de documento.
 */
export const EM_MONO: TipoDoDado[] = [
  'telefone',
  'email',
  'processo',
  'participacao_societaria',
  'rede_social',
  'outro',
];

/**
 * O exemplo que aparece dentro do campo, por tipo.
 *
 * ⚠️ **Vale mais nos quatro novos do que nos antigos.** "Estado civil" não diz
 * se a casa escreve *casado* ou *CASADO*, nem se união estável entra ali — e
 * campo sem exemplo é campo preenchido de dez jeitos diferentes, que é
 * exatamente o que a regra de caixa alta da casa existe para evitar.
 */
const PISTAS: Partial<Record<TipoDoDado, string>> = {
  estado_civil: 'Casado, solteiro, divorciado, viúvo, união estável',
  regime_de_bens: 'Comunhão parcial, universal, separação total',
  obito: 'DD/MM/AAAA, ou o indício a confirmar em certidão',
  situacao_cadastral: 'Regular, suspensa, cancelada, titular falecido',
  telefone: '(11) 90000-0000',
  email: 'nome@exemplo.com',
  endereco: 'Rua, número, bairro, cidade/UF',
  profissao: 'Cargo ou profissão',
  processo: '0000000-00.0000.0.00.0000',
  participacao_societaria: 'Razão social — CNPJ',
  historico_profissional: 'Cargo ou registro — empresa ou órgão',
  rede_social: 'https://…',
  ppe: 'Sim',
  probabilidade_negociacao: '0 a 100',
};

/**
 * O exemplo do campo de NOTA, quando o tipo tem um formato esperado.
 *
 * ⚠️ **A faixa do índice mora na nota, e sem isto ninguém adivinha.** É ela que
 * dá a cor à barra — alta verde, média laranja, baixa vinho. Nota genérica
 * ("Nota (opcional)") faria o campo ser preenchido com qualquer coisa, e a
 * barra sairia sem cor sem que a tela dissesse por quê.
 */
const PISTAS_NOTA: Partial<Record<TipoDoDado, string>> = {
  probabilidade_negociacao: 'Alta, Média ou Baixa — dá a cor da barra',
};

/*
 * ⚠️ **Um por vez, em fila** (a razão está em `buscar`, acima). Extraída em
 * 19/09/2026 para o tópico no desenho de Imóveis (`QualificacaoDoCaso`) usar a
 * MESMA fila — nada mudou nela. Devolve quantos foram pesquisados.
 */
export async function buscarEmFila(ids: string[], setBuscando: (id: string | null) => void): Promise<number> {
  let feitos = 0;
  for (const id of ids) {
    setBuscando(id);
    try {
      await apiInvestigados.buscar(id);
      feitos += 1;
    } catch (e) {
      avisarErro(e);
    }
  }
  setBuscando(null);
  return feitos;
}

/**
 * A falha que ainda vale, por funcionalidade — só das fontes desta seção.
 * Extraída em 19/09/2026 do cartão da parte, sem mudança (o porquê está lá).
 */
export function falhasVigentesDe(consultas: ConsultaExterna[]): ConsultaExterna[] {
  const ultima = new Map<string, ConsultaExterna>();
  for (const c of [...consultas].sort((a, b) => (a.criadoEm < b.criadoEm ? -1 : 1))) {
    /*
     * ⚠️ **Só as fontes DESTA seção — corrigido em 21/08/2026.** O bloco
     * mostrava toda `ConsultaExterna` do investigado, sem olhar a fonte. Com
     * a Infosimples ligada, as falhas do ÍNDICE DE ENDIVIDAMENTO passaram a
     * aparecer aqui, embaixo do nome, numa seção que não as causou e não as
     * resolve.
     *
     * **A falha continua visível — na seção que a produziu.** Erro engolido é
     * erro que não existe; erro na seção errada é ruído que se aprende a
     * ignorar, o que dá no mesmo.
     */
    if (!FONTES_DESTA_SECAO.has(c.fonte)) continue;
    ultima.set(c.funcionalidade, c);
  }
  return [...ultima.values()].filter((c) => c.falha);
}

/**
 * O que o bloco de campos livres precisa saber para viver sob um investigado.
 *
 * ⚠️ **Vem de cima inteiro, e não é montado aqui.** A lista de anotações e o
 * "recarregar" são do editor do relatório; esta tela só sabe de investigado.
 */
export type CamposDoInvestigado = {
  relatorioId: string;
  secaoId: string;
  lista: AnotacaoDaSecao[];
  travado: boolean;
  aoMudar: () => Promise<void> | void;
};

/**
 * Os atos societários da empresa, levados à qualificação — 15/09/2026 (o porquê
 * está no cartão da parte). Extraído em 19/09/2026 para o tópico no desenho de
 * Imóveis usar a MESMA peça; `desenho="imv"` troca só a classe dos botões.
 */
export function LevarEvolucao({
  investigado,
  evolucao,
  anotacoes,
  desenho,
}: {
  investigado: Investigado;
  evolucao: EvolucaoDaEmpresa;
  anotacoes: CamposDoInvestigado;
  desenho?: 'imv';
}) {
  const [levando, setLevando] = useState(false);
  /* ⚠️ O campo se reconhece pelo título, lido da lista viva — apagado à mão, o botão volta a criar. */
  const campoDaEvolucao =
    anotacoes.lista.find((a) => a.investigadoId === investigado.id && (a.titulo ?? '').startsWith(TITULO_DA_EVOLUCAO)) ?? null;

  async function levarEvolucao() {
    const deNovo = !!campoDaEvolucao;
    setLevando(true);
    try {
      await apiRelatorios.levarEvolucao(anotacoes.relatorioId, investigado.id);
      await anotacoes.aoMudar();
      avisar(
        deNovo
          ? 'Outro campo criado com o texto atual. O anterior ficou como estava.'
          : 'Evolução do quadro levada à qualificação. Revise e acrescente os prints no campo.',
      );
    } catch (e) {
      avisarErro(e);
    } finally {
      setLevando(false);
    }
  }

  return (
    <div className="evolucao-na-qualificacao">
      <p>
        <strong className="dado">{evolucao.atos}</strong> {evolucao.atos === 1 ? 'ato lido' : 'atos lidos'}.{' '}
        {campoDaEvolucao
          ? 'Já está no campo de escrita desta empresa, logo abaixo.'
          : 'Ainda não foi levada à qualificação.'}
      </p>
      <details>
        <summary>Ver o texto que vai ao campo</summary>
        <pre className="previa-da-secao">{evolucao.texto}</pre>
      </details>
      {!anotacoes.travado && (
        <button
          type="button"
          className={
            desenho === 'imv'
              ? `imv-bt${campoDaEvolucao ? '' : ' primario'}`
              : campoDaEvolucao
                ? 'botao-secundario'
                : 'botao-primario'
          }
          disabled={levando}
          onClick={() => void levarEvolucao()}
        >
          {levando ? 'Levando…' : campoDaEvolucao ? 'Levar de novo, em outro campo' : 'Levar à qualificação desta empresa'}
        </button>
      )}
      <p className="ajuda">
        {campoDaEvolucao
          ? 'Cria outro campo com a evolução lida hoje, logo abaixo. O que já está escrito, e os prints, ficam como estão; apague à mão o que não servir.'
          : `Cria um campo de escrita com a evolução do quadro, que sai no relatório sob o título “${TITULO_DA_EVOLUCAO} — ${investigado.nome}”. Revise e acrescente os prints nele.`}
      </p>
    </div>
  );
}

/**
 * O índice de probabilidade de negociação, em barra — pedido do Richard,
 * 21/08/2026.
 *
 * ⚠️ **A barra é decoração; o número é o dado.** O percentual sai escrito ao
 * lado, sempre. Barra sem número obriga a medir com o olho, e "aproximadamente
 * 71%" vira "uns 70 e pouco" na hora de escrever o relatório.
 *
 * ⚠️ **Valor que não é número vira texto puro**, sem barra. O campo é livre e
 * alguém pode digitar "Alta"; desenhar uma barra de largura zero diria
 * "nenhuma probabilidade", que é o oposto.
 *
 * ⚠️ **A cor é o ouro da casa, e não semáforo.** Verde-amarelo-vermelho faria a
 * tela opinar sobre um número que a própria fonte diz não ser opinião nem
 * sugestão — e "cor não se inventa": só existe o que está em `tokens.css`.
 */
export function BarraDePorcentagem({ valor, faixa }: { valor: string; faixa: string | null }) {
  const n = Number(String(valor).replace('%', '').replace(',', '.').trim());
  if (!Number.isFinite(n)) return <span>{valor}</span>;
  const pct = Math.max(0, Math.min(100, n));
  const f = lerFaixa(faixa);
  return (
    <span className={`barra-pct barra-pct-${f ?? 'sem-faixa'}`}>
      <span
        className="barra-pct-trilho"
        role="img"
        aria-label={`${pct}% de probabilidade${f ? `, faixa ${f}` : ''}`}
      >
        <span className="barra-pct-cheio" style={{ width: `${pct}%` }} />
      </span>
      <span className="dado">{pct}%</span>
      {f && <span className="barra-pct-faixa">{ROTULO_DA_FAIXA[f]}</span>}
    </span>
  );
}

const ROTULO_DA_FAIXA = { alta: 'Alta', media: 'Média', baixa: 'Baixa' } as const;

/**
 * Lê a faixa escrita na nota do dado.
 *
 * ⚠️ **A fonte não devolve só a palavra.** Ela imprime *"Alta - Cadastros com
 * esse perfil têm aproximadamente 71% de chance…"*, então a comparação é por
 * início, sem acento e sem caixa — comparar a nota inteira nunca casaria.
 *
 * ⚠️ **Sem faixa, a barra NÃO ganha cor de semáforo.** Deduzir a faixa do
 * percentual seria o Hub inventando o corte entre alta e média — número que a
 * fonte calcula com critério dela, e que o Hub não conhece. Sem faixa a barra
 * fica no ouro da casa, que não afirma nada.
 */
function lerFaixa(nota: string | null): 'alta' | 'media' | 'baixa' | null {
  const t = (nota ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
  if (t.startsWith('alta')) return 'alta';
  if (t.startsWith('media')) return 'media';
  if (t.startsWith('baixa')) return 'baixa';
  return null;
}

/**
 * A linha de acrescentar à qualificação — extraída em 19/09/2026 do bloco da
 * QUALIFICAÇÃO, sem mudança, para o tópico no desenho de Imóveis usar a MESMA.
 */
export function FormularioDeDado({
  investigadoId,
  aoMudar,
  titulo = 'Acrescentar à qualificação',
}: {
  investigadoId: string;
  aoMudar: () => Promise<void>;
  titulo?: string;
}) {
  const [tipo, setTipo] = useState<TipoDoDado>('telefone');
  const [valor, setValor] = useState('');
  const [nota, setNota] = useState('');

  async function acrescentar(e: React.FormEvent) {
    e.preventDefault();
    if (!valor.trim()) return;
    try {
      await apiInvestigados.acrescentarDado(investigadoId, {
        tipo,
        valor: valor.trim(),
        nota: nota.trim() || null,
      });
      setValor('');
      setNota('');
      await aoMudar();
    } catch (err) {
      avisarErro(err);
    }
  }

  /* ⭐ No desenho novo desde 18/09/2026 (o bloco de lançar): cada campo diz o que é. */
  return (
    <BlocoDeLancar
      titulo={titulo}
      aoEnviar={(e) => void acrescentar(e)}
      acoes={
        <button type="submit" className="imv-bt primario" disabled={!valor.trim()}>
          <Icone nome="mais" tamanho={14} /> Acrescentar
        </button>
      }
    >
      <CampoDoPasso rotulo="Tipo do dado" col={2}>
        <select value={tipo} onChange={(e) => setTipo(e.target.value as TipoDoDado)}>
          {DA_QUALIFICACAO.map((t) => (
            <option key={t} value={t}>
              {TIPOS_DE_DADO[t]}
            </option>
          ))}
        </select>
      </CampoDoPasso>
      <CampoDoPasso rotulo="Valor" obrigatorio col={4} mono={EM_MONO.includes(tipo)}>
        <input value={valor} onChange={(e) => setValor(e.target.value)} placeholder={PISTAS[tipo] ?? ''} />
      </CampoDoPasso>
      <CampoDoPasso rotulo="Nota" col={6}>
        <input value={nota} onChange={(e) => setNota(e.target.value)} placeholder={PISTAS_NOTA[tipo] ?? 'Opcional'} />
      </CampoDoPasso>
    </BlocoDeLancar>
  );
}

/**
 * ⭐ **Os vínculos que se pode acrescentar à mão — 09/09/2026.**
 *
 * ⚠️ **A ordem não é alfabética, e é de propósito:** cônjuge e filho(a) vêm
 * primeiro porque são o caso que ele descreveu e o que a regra da casa manda
 * olhar sempre — *"cônjuge e filhos entram sempre, mesmo sem suspeita"*.
 *
 * ⚠️ **Os rótulos são os MESMOS que a consulta grava** ("Irmão(ã)", "Cônjuge"),
 * senão a mesma pessoa apareceria escrita de dois jeitos na mesma lista,
 * conforme tivesse vindo de fora ou sido digitada.
 */
const VINCULOS_A_MAO: Array<{ valor: string; rotulo: string }> = [
  { valor: 'conjuge', rotulo: 'Cônjuge' },
  { valor: 'filho', rotulo: 'Filho(a)' },
  { valor: 'pai', rotulo: 'Pai' },
  { valor: 'mae', rotulo: 'Mãe' },
  { valor: 'irmao', rotulo: 'Irmão(ã)' },
  { valor: 'avo', rotulo: 'Avô(ó)' },
  { valor: 'neto', rotulo: 'Neto(a)' },
  { valor: 'bisavo', rotulo: 'Bisavô(ó)' },
  { valor: 'bisneto', rotulo: 'Bisneto(a)' },
  { valor: 'tio', rotulo: 'Tio(a)' },
  { valor: 'sobrinho', rotulo: 'Sobrinho(a)' },
  /* ⚠️ Os dois de baixo NÃO são grau: viram `societario` e "outros" no
     servidor, e não entram na árvore genealógica. */
  { valor: 'socio', rotulo: 'Sócio(a)' },
  { valor: 'outro', rotulo: 'Outro vínculo' },
];

type AcaoNaConexao = {
  investigadoId: string;
  conexao: { id: string; nome: string };
  aoFechar: () => void;
  aoMudar: () => Promise<void>;
};

/** "Trazer para o caso?" — extraída em 19/09/2026 das CONEXÕES, sem mudança de texto nem de rota. */
export function TrazerParaOCaso({ investigadoId, conexao, aoFechar, aoMudar }: AcaoNaConexao) {
  return (
    <Confirmar
      titulo="Trazer para o caso?"
      descricao={`${conexao.nome} passa a ser investigado deste caso, com o vínculo de origem preservado. ⚠️ Consome a cota do nível — e quem confirma que o vínculo é relevante é quem apura, não a consulta.`}
      rotuloAcao="Trazer"
      aoConfirmar={async () => {
        try {
          await apiInvestigados.promover(investigadoId, conexao.id);
          aoFechar();
          await aoMudar();
          avisar(`${conexao.nome} entrou no caso.`);
        } catch (e) {
          avisarErro(e);
        }
      }}
      aoFechar={aoFechar}
    />
  );
}

/** "Apagar este vínculo?" — extraída em 19/09/2026 das CONEXÕES, sem mudança de texto nem de rota. */
export function ApagarVinculo({ investigadoId, conexao, aoFechar, aoMudar }: AcaoNaConexao) {
  return (
    <Confirmar
      titulo="Apagar este vínculo?"
      descricao={`${conexao.nome} sai do mapa de vínculos deste investigado. Só se apaga o que foi digitado à mão — o que veio de consulta é o registro do que a fonte respondeu.`}
      rotuloAcao="Apagar"
      aoConfirmar={async () => {
        try {
          await apiInvestigados.apagarConexao(investigadoId, conexao.id);
          aoFechar();
          await aoMudar();
          avisar(`${conexao.nome} saiu dos vínculos.`);
        } catch (e) {
          avisarErro(e);
        }
      }}
      aoFechar={aoFechar}
    />
  );
}

/**
 * A linha de acrescentar vínculo — 09/09/2026.
 *
 * ⚠️ **O documento é OPCIONAL e conferido quando vem.** Um filho menor não tem
 * CPF em base nenhuma, e exigi-lo aqui recusaria justamente o caso que o botão
 * existe para atender. Vindo, o dígito verificador é conferido: CPF errado vira
 * vínculo que nenhuma consulta futura casa, e o parente ficaria duplicado.
 */
export function FormularioDeVinculo({
  investigadoId,
  aoMudar,
  titulo = 'Acrescentar vínculo',
}: {
  investigadoId: string;
  aoMudar: () => Promise<void>;
  /* O tópico no desenho de Imóveis diz de quem é (19/09/2026); o resto é o mesmo. */
  titulo?: string;
}) {
  const [tipo, setTipo] = useState<'pf' | 'pj'>('pf');
  const [nome, setNome] = useState('');
  const [documento, setDocumento] = useState('');
  const [vinculo, setVinculo] = useState('conjuge');
  const [detalhe, setDetalhe] = useState('');
  const [salvando, setSalvando] = useState(false);

  async function acrescentar(e: React.FormEvent) {
    e.preventDefault();
    if (!nome.trim() || salvando) return;
    setSalvando(true);
    try {
      /* ⚠️ O servidor é quem traduz vínculo em grau e rótulo — a tela manda a
         escolha, não a classificação. Ver `investigados.ts`. */
      const ehGrau = vinculo !== 'socio' && vinculo !== 'outro';
      await apiInvestigados.acrescentarConexao(investigadoId, {
        nome: nome.trim(),
        tipo,
        documento: documento.trim() || null,
        grau: ehGrau ? (vinculo as GrauDeParentesco) : null,
        societario: vinculo === 'socio',
        detalhe: detalhe.trim() || null,
      });
      setNome('');
      setDocumento('');
      setDetalhe('');
      await aoMudar();
      avisar('Vínculo acrescentado.');
    } catch (err) {
      avisarErro(err);
    } finally {
      setSalvando(false);
    }
  }

  /* ⭐ No desenho novo desde 18/09/2026 (o bloco de lançar): cada campo diz o que é. */
  return (
    <BlocoDeLancar
      titulo={titulo}
      aoEnviar={(e) => void acrescentar(e)}
      nota={
        <>
          <p>
            Para quem a investigação encontrou <strong>lendo um documento</strong> — certidão, escritura, contrato social — e a consulta não trouxe.
            Parentesco entra na <strong>árvore genealógica</strong> na hora; <strong>não consome a cota do nível</strong>, porque só entra no caso quem for
            trazido pelo botão da linha.
          </p>
          {/*
            ⚠️ **O ORGANOGRAMA não se desenha daqui, e a tela precisa dizer.** Ele
            lê as participações societárias da QUALIFICAÇÃO; uma empresa
            acrescentada aqui registra o vínculo e **não redesenha o mapa** —
            quem esperasse o contrário veria o botão funcionar e o desenho não
            mudar, que é o pior tipo de defeito: o que não avisa.
          */}
          {tipo === 'pj' && (
            <p>
              ⚠️ O <strong>organograma</strong> se desenha das participações societárias, na QUALIFICAÇÃO acima — acrescentar a empresa aqui registra o
              vínculo, mas não redesenha o mapa.
            </p>
          )}
        </>
      }
      acoes={
        <button type="submit" className="imv-bt primario" disabled={!nome.trim() || salvando}>
          <Icone nome="mais" tamanho={14} /> {salvando ? 'Gravando…' : 'Acrescentar'}
        </button>
      }
    >
      <div className="imv-campo c2">
        <span className="imv-rotulo">Pessoa ou empresa</span>
        <div className="imv-seg" role="radiogroup" aria-label="Pessoa ou empresa">
          {(['pf', 'pj'] as const).map((t) => (
            <button key={t} type="button" role="radio" aria-checked={tipo === t} className={`imv-op neutro${tipo === t ? ' on' : ''}`} onClick={() => setTipo(t)}>
              <i />
              {t === 'pf' ? 'Pessoa' : 'Empresa'}
            </button>
          ))}
        </div>
      </div>
      <CampoDoPasso rotulo="Vínculo" col={2}>
        <select value={vinculo} onChange={(e) => setVinculo(e.target.value)}>
          {VINCULOS_A_MAO.map((v) => (
            <option key={v.valor} value={v.valor}>
              {v.rotulo}
            </option>
          ))}
        </select>
      </CampoDoPasso>
      <CampoDoPasso rotulo={tipo === 'pf' ? 'CPF' : 'CNPJ'} col={2} mono>
        <input value={documento} onChange={(e) => setDocumento(e.target.value)} placeholder="Opcional" />
      </CampoDoPasso>
      <CampoDoPasso rotulo={tipo === 'pf' ? 'Nome completo' : 'Razão social'} obrigatorio col={3}>
        <input value={nome} onChange={(e) => setNome(e.target.value)} />
      </CampoDoPasso>
      <CampoDoPasso rotulo="Onde apareceu" col={3}>
        <input value={detalhe} onChange={(e) => setDetalhe(e.target.value)} placeholder="Opcional" />
      </CampoDoPasso>
    </BlocoDeLancar>
  );
}
